/**
 * On-chain ABIs + permit tuple builders for the Macro #29 Artwork/Auction
 * escrows (Base Sepolia). Byte-exact to TheGridArtworkEscrow /
 * TheGridAuctionEscrow struct field ORDER — viem encodes tuples from objects by
 * the ABI component order, so the backend-signed EIP-712 permit and the on-chain
 * calldata are identical. The frontend NEVER invents a permit or signature; it
 * only forwards the authority-signed permit to the connected wallet.
 */
import type { Abi } from "viem";
import type {
  ArtworkOpenPermit,
  AuctionBidPermit,
  AuctionCreatePermit,
} from "../../lib/marketplace-api";

// ---------------------------------------------------------------------
// TheGridArtworkEscrow — open(OpenPermit, bytes) + release(bytes32, bytes)
// ---------------------------------------------------------------------
export const ARTWORK_ESCROW_ABI = [
  {
    type: "function",
    name: "open",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "p",
        type: "tuple",
        components: [
          { name: "settlementId", type: "bytes32" },
          { name: "artworkId", type: "bytes32" },
          { name: "buyer", type: "address" },
          { name: "seller", type: "address" },
          { name: "creator", type: "address" },
          { name: "currency", type: "address" },
          { name: "grossPrice", type: "uint256" },
          { name: "royaltyBps", type: "uint16" },
          { name: "placementCommitment", type: "bytes32" },
          { name: "ownerRevision", type: "uint256" },
          { name: "expiryRealTs", type: "uint256" },
        ],
      },
      { name: "signerSig", type: "bytes" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "release",
    stateMutability: "nonpayable",
    inputs: [
      { name: "settlementId", type: "bytes32" },
      { name: "releaseSig", type: "bytes" },
    ],
    outputs: [],
  },
] as const satisfies Abi;

// ---------------------------------------------------------------------
// TheGridAuctionEscrow — createAuction / bid / settle / cancel
// ---------------------------------------------------------------------
export const AUCTION_ESCROW_ABI = [
  {
    type: "function",
    name: "createAuction",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "p",
        type: "tuple",
        components: [
          { name: "auctionId", type: "bytes32" },
          { name: "artworkId", type: "bytes32" },
          { name: "seller", type: "address" },
          { name: "creator", type: "address" },
          { name: "currency", type: "address" },
          { name: "reservePrice", type: "uint256" },
          { name: "minIncrement", type: "uint256" },
          { name: "royaltyBps", type: "uint16" },
          { name: "startMarketTs", type: "uint256" },
          { name: "endMarketTs", type: "uint256" },
          { name: "antiSnipWindow", type: "uint256" },
          { name: "placementCommitment", type: "bytes32" },
          { name: "ownerRevision", type: "uint256" },
          { name: "expiryRealTs", type: "uint256" },
        ],
      },
      { name: "signerSig", type: "bytes" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "bid",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "b",
        type: "tuple",
        components: [
          { name: "auctionId", type: "bytes32" },
          { name: "bidder", type: "address" },
          { name: "amount", type: "uint256" },
          { name: "nonce", type: "uint256" },
          { name: "expiryRealTs", type: "uint256" },
        ],
      },
      { name: "signerSig", type: "bytes" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "settle",
    stateMutability: "nonpayable",
    inputs: [{ name: "auctionId", type: "bytes32" }],
    outputs: [],
  },
  {
    type: "function",
    name: "cancel",
    stateMutability: "nonpayable",
    inputs: [{ name: "auctionId", type: "bytes32" }],
    outputs: [],
  },
] as const satisfies Abi;

const hex = (s: string): `0x${string}` => s.toLowerCase() as `0x${string}`;

/** Build the exact on-chain OpenPermit tuple from the authority permit. */
export function buildArtworkOpenPermit(p: ArtworkOpenPermit) {
  return {
    settlementId: p.settlementId as `0x${string}`,
    artworkId: p.artworkId as `0x${string}`,
    buyer: hex(p.buyer),
    seller: hex(p.seller),
    creator: hex(p.creator),
    currency: hex(p.currency),
    grossPrice: BigInt(p.grossPrice),
    royaltyBps: p.royaltyBps,
    placementCommitment: p.placementCommitment as `0x${string}`,
    ownerRevision: BigInt(p.ownerRevision),
    expiryRealTs: BigInt(p.expiryRealTs),
  } as const;
}

/** Build the exact on-chain AuctionPermit tuple from the authority permit. */
export function buildAuctionCreatePermit(p: AuctionCreatePermit) {
  return {
    auctionId: p.auctionId as `0x${string}`,
    artworkId: p.artworkId as `0x${string}`,
    seller: hex(p.seller),
    creator: hex(p.creator),
    currency: hex(p.currency),
    reservePrice: BigInt(p.reservePrice),
    minIncrement: BigInt(p.minIncrement),
    royaltyBps: p.royaltyBps,
    startMarketTs: BigInt(p.startMarketTs),
    endMarketTs: BigInt(p.endMarketTs),
    antiSnipWindow: BigInt(p.antiSnipWindow),
    placementCommitment: p.placementCommitment as `0x${string}`,
    ownerRevision: BigInt(p.ownerRevision),
    expiryRealTs: BigInt(p.expiryRealTs),
  } as const;
}

/** Build the exact on-chain BidPermit tuple from the authority permit. */
export function buildAuctionBidPermit(p: AuctionBidPermit) {
  return {
    auctionId: p.auctionId as `0x${string}`,
    bidder: hex(p.bidder),
    amount: BigInt(p.amount),
    nonce: BigInt(p.nonce),
    expiryRealTs: BigInt(p.expiryRealTs),
  } as const;
}
